import { firstWord, localDay, type Difficulty, type GameState, type Repeat, type Task, type TaskType } from '@thicket/core';
import { html, nothing } from 'lit-html';
import { app, update } from './app.js';

const DAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DIFFS: Difficulty[] = ['trivial', 'easy', 'medium', 'hard'];

interface Draft {
  for: string | null;
  type: TaskType;
  text: string;
  notes: string;
  difficulty: Difficulty;
  label: string;
  scheduleKind: 'weekly' | 'everyNDays';
  days: number[];
  every: number;
  dueDate: string;
  up: boolean;
  down: boolean;
  paused: boolean;
  /** New checklist items while creating a task. */
  items: string[];
  newItem: string;
}

let draft: Draft | null = null;

function fromTask(t: Task | null, s: GameState, newType: TaskType = 'daily'): Draft {
  const base: Draft = {
    for: t?.id ?? null, type: t?.type ?? newType, text: t?.text ?? '', notes: t?.notes ?? '', difficulty: t?.difficulty ?? 'easy',
    label: '', scheduleKind: 'weekly', days: [0, 1, 2, 3, 4, 5, 6], every: 2, dueDate: '', up: true, down: true, paused: false,
    items: [], newItem: '',
  };
  if (t?.type === 'daily') {
    base.label = t.label;
    base.paused = t.paused;
    if (t.repeat.kind === 'weekly') base.days = [...t.repeat.days];
    else Object.assign(base, { scheduleKind: 'everyNDays', every: t.repeat.every });
  }
  if (t?.type === 'todo') base.dueDate = t.dueDate ?? '';
  if (t?.type === 'habit') Object.assign(base, { up: t.up, down: t.down });
  void s;
  return base;
}

const close = () => update(() => { app.sheet = null; draft = null; });
const redraw = () => update();

function repeatOf(d: Draft, today: string): Repeat {
  return d.scheduleKind === 'everyNDays'
    ? { kind: 'everyNDays', every: Math.max(1, Math.min(365, d.every || 1)), startDate: today }
    : { kind: 'weekly', days: d.days.length ? [...d.days].sort() : [0, 1, 2, 3, 4, 5, 6] };
}

function save(s: GameState, task: Task | null) {
  const d = draft!;
  const text = d.text.trim();
  if (!text) return;
  const today = localDay(app.store.now(), s.profile);
  if (!task) {
    const t: Record<string, unknown> = { type: d.type, text, difficulty: d.difficulty };
    if (d.notes.trim()) t.notes = d.notes.trim();
    if (d.type === 'daily') {
      t.repeat = repeatOf(d, today);
      t.label = d.label.trim();
    }
    if (d.type === 'todo' && d.dueDate) t.dueDate = d.dueDate;
    if (d.type === 'habit') Object.assign(t, { up: d.up, down: d.down });
    if (d.type !== 'habit' && d.items.length) t.checklist = d.items;
    if (app.store.act({ type: 'createTask', task: t as never })) close();
    return;
  }
  const patch: Record<string, unknown> = {};
  if (text !== task.text) patch.text = text;
  if (d.notes !== task.notes) patch.notes = d.notes;
  if (d.difficulty !== task.difficulty) patch.difficulty = d.difficulty;
  if (task.type === 'daily') {
    const repeat = repeatOf(d, today);
    const same = JSON.stringify({ ...repeat, startDate: undefined }) === JSON.stringify({ ...task.repeat, startDate: undefined });
    if (!same) patch.repeat = repeat;
    if (d.label.trim() !== task.label) patch.label = d.label.trim();
    if (d.paused !== task.paused) patch.paused = d.paused;
  }
  if (task.type === 'todo' && (d.dueDate || null) !== task.dueDate) patch.dueDate = d.dueDate || null;
  if (task.type === 'habit') {
    if (d.up !== task.up) patch.up = d.up;
    if (d.down !== task.down) patch.down = d.down;
  }
  if (Object.keys(patch).length && !app.store.act({ type: 'updateTask', id: task.id, patch: patch as never })) return;
  close();
}

const input = (key: keyof Draft) => (e: Event) => {
  (draft as any)[key] = (e.target as HTMLInputElement).value;
};

function seg<T extends string>(options: readonly T[], current: T, set: (v: T) => void, label: (v: T) => string = (v) => v) {
  return html`<div class="seg" role="group">${options.map((o) => html`<button type="button" aria-pressed=${o === current}
    @click=${() => { set(o); redraw(); }}>${label(o)}</button>`)}</div>`;
}

function checklist(s: GameState, task: Task | null) {
  const d = draft!;
  if (d.type === 'habit') return nothing;
  const live = task ? s.checklist.filter((i) => i.taskId === task.id).sort((a, b) => a.position - b.position) : [];
  const add = () => {
    const text = d.newItem.trim();
    if (!text) return;
    if (task) app.store.act({ type: 'addChecklistItem', taskId: task.id, text });
    else d.items.push(text);
    d.newItem = '';
    redraw();
  };
  return html`<div class="field"><span class="small" style="font-weight:600">Checklist</span>
    <ul class="list" style="margin:6px 0">
      ${task
        ? live.map((i) => html`<li class="row" style="min-height:44px">
            <button class="check" style="width:36px;height:36px;font-size:18px" aria-pressed=${i.done} aria-label=${`Toggle ${i.text}`}
              @click=${() => app.store.act({ type: 'toggleChecklistItem', id: i.id })}>${i.done ? '✓' : ''}</button>
            <span style="flex:1">${i.text}</span>
            <button class="link" aria-label=${`Remove ${i.text}`} @click=${() => app.store.act({ type: 'removeChecklistItem', id: i.id })}>Remove</button></li>`)
        : d.items.map((text, n) => html`<li class="row" style="min-height:44px"><span style="flex:1">${text}</span>
            <button class="link" @click=${() => { d.items.splice(n, 1); redraw(); }}>Remove</button></li>`)}
    </ul>
    <div class="actions"><input type="text" placeholder="Add a step" .value=${d.newItem} @input=${input('newItem')}
      @keydown=${(e: KeyboardEvent) => e.key === 'Enter' && (e.preventDefault(), add())} style="flex:1"><button type="button" @click=${add}>Add</button></div>
  </div>`;
}

export function taskSheet(s: GameState, taskId: string | null, newType: TaskType = 'daily') {
  const task = taskId ? s.tasks.find((t) => t.id === taskId) ?? null : null;
  if (taskId && !task) return nothing;
  if (!draft || draft.for !== (task?.id ?? null)) draft = fromTask(task, s, newType);
  const d = draft;
  const kind = { daily: 'Daily (grows a tree)', habit: 'Habit (a raised-bed plant)', todo: "To-do (a wildflower)" };

  return html`<div class="scrim" @click=${(e: Event) => e.target === e.currentTarget && close()}>
    <div class="sheet" role="dialog" aria-modal="true" aria-label=${task ? `Edit ${task.type}` : `New ${d.type === 'todo' ? 'to-do' : d.type}`}>
      <div class="sheet-head"><h2>${task ? `Edit ${task.type === 'todo' ? 'to-do' : task.type}` : `New ${d.type === 'todo' ? 'to-do' : d.type}`}</h2><button @click=${close}>Close</button></div>
      <form class="form" @submit=${(e: Event) => { e.preventDefault(); save(s, task); }}>
        ${task ? html`<p class="muted small" style="margin:0">${kind[task.type]}</p>` : seg(['daily', 'habit', 'todo'] as const, d.type, (v) => (d.type = v), (v) => kind[v].split(' (')[0]!)}
        <label class="field">What is it?<input type="text" required maxlength="200" .value=${d.text} @input=${input('text')} ?autofocus=${!task}></label>
        <div class="field"><span class="small" style="font-weight:600">Difficulty</span>
          ${seg(DIFFS, d.difficulty, (v) => (d.difficulty = v), (v) => v[0]!.toUpperCase() + v.slice(1))}</div>

        ${d.type === 'daily' ? html`
          <div class="field"><span class="small" style="font-weight:600">Repeats</span>
            ${seg(['weekly', 'everyNDays'] as const, d.scheduleKind, (v) => (d.scheduleKind = v), (v) => (v === 'weekly' ? 'On days' : 'Every few days'))}
            ${d.scheduleKind === 'weekly'
              ? html`<div class="seg days" style="margin-top:6px">${DAYS.map((label, i) => html`<button type="button" aria-label=${DAY_NAMES[i]} aria-pressed=${d.days.includes(i)}
                  @click=${() => { d.days = d.days.includes(i) ? d.days.filter((x) => x !== i) : [...d.days, i]; redraw(); }}>${label}</button>`)}</div>`
              : html`<label class="field" style="margin-top:6px">Every how many days?<input type="number" min="1" max="365" .value=${String(d.every)}
                  @input=${(e: Event) => (d.every = Number((e.target as HTMLInputElement).value))}></label>`}
          </div>
          <label class="field">Tree label <span class="muted small" style="font-weight:400">(up to ${app.store.rules.labelMax} letters)</span>
            <input type="text" maxlength=${app.store.rules.labelMax} .value=${d.label} @input=${input('label')} placeholder=${firstWord(d.text, app.store.rules.labelMax) || 'e.g. Gym'}></label>
          <p class="small muted" style="margin:-6px 0 0">Painted on the tree. Leave it empty for a bare tree; tapping the tree opens this daily either way.</p>
          ${task ? html`<label class="actions"><input type="checkbox" .checked=${d.paused} @change=${(e: Event) => (d.paused = (e.target as HTMLInputElement).checked)}> Rest this daily (its streak waits)</label>` : nothing}
        ` : nothing}

        ${d.type === 'todo' ? html`<label class="field">Due date (optional)<input type="date" .value=${d.dueDate} @input=${input('dueDate')}></label>` : nothing}

        ${d.type === 'habit' ? html`<div class="actions">
          <label class="actions"><input type="checkbox" .checked=${d.up} @change=${(e: Event) => (d.up = (e.target as HTMLInputElement).checked)}> + button</label>
          <label class="actions"><input type="checkbox" .checked=${d.down} @change=${(e: Event) => (d.down = (e.target as HTMLInputElement).checked)}> − button</label>
        </div>` : nothing}

        <label class="field">Notes<textarea .value=${d.notes} @input=${input('notes')}></textarea></label>
        ${checklist(s, task)}

        <div class="actions" style="justify-content:space-between">
          <button type="submit" class="primary">${task ? 'Save' : 'Add'}</button>
          ${task ? html`<button type="button" class="danger" @click=${() => { if (app.store.act({ type: 'archiveTask', id: task.id })) close(); }}>Archive</button>` : nothing}
        </div>
        ${task ? html`<p class="muted small" style="margin:0">Archiving removes it from your lists. Its plant and history stay, and you can bring it back in Settings.</p>` : nothing}
      </form>
    </div>
  </div>`;
}
